import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { appointmentService } from '../services/appointmentService'
import { userService } from '../services/userService'
import { Patient } from '../types/patient'
import { Appointment, AppointmentStatus } from '../types/appointment'
import { formatPatientName } from '../utils/name'
import { Consultation } from '../types/consultation'
// GMS eligibility is surfaced in the header, not as a separate card
import { DPSDrugChecker } from '../components/DPSDrugChecker'
import { ConsultationForm } from '../components/ConsultationForm'
import { QuickStartConsultationForm } from '../components/QuickStartConsultationForm'
import { ConsultationList } from '../components/ConsultationList'
import { LabResultList } from '../components/LabResultList'
import { Modal } from '../components/Modal'
import { useLetterWriting } from '../contexts/LetterWritingContext'
import { AppointmentScheduler } from '../components/AppointmentScheduler'
import { AppointmentCancelModal } from '../components/AppointmentCancelModal'
import { PatientFileList } from '../components/PatientFileList'
import { PatientFileUpload } from '../components/PatientFileUpload'
import { useSidebar } from '../contexts/SidebarContext'
import { formatDate } from '../utils/locale'
import { calculateGestationalAge } from '../utils/dueDateCalculator'
import { buildSanitizedConsultationContext } from '../utils/aiContextUtils'
import { PatientClinicalHistory } from '../components/PatientClinicalHistory'
import { PatientAuditHistory } from '../components/PatientAuditHistory'
import { PreventiveServicesAlerts } from '../components/PreventiveServicesAlerts'
import { useGeminiContext } from '../contexts/GeminiContextContext'
import { useAuth } from '../contexts/AuthContext'
import { VitalSignsSummary } from '../components/VitalSignsSummary'
import { VitalSignsChart } from '../components/VitalSignsChart'
import { GrowthCentileChartsDialog } from '../components/GrowthCentileChartsDialog'
import { VitalSignsList } from '../components/VitalSignsList'
import { VitalSignsForm } from '../components/VitalSignsForm'
import { vitalSignsService } from '../services/vitalSignsService'
import { VitalSignsFormData, VitalSigns } from '../types/vitalSigns'
import type { GMSStatus } from '../types/gms'
import { getUnitPreference } from '../utils/userPreferences'
import { calculateAgeInMonths } from '../utils/normativeData'
import { getDisplayValue, formatTemperature, formatHeight, formatWeight } from '../utils/vitalSignsUtils'
import { PatientAllergies } from '../components/PatientAllergies'
import { PatientMedications } from '../components/PatientMedications'
import { PatientImmunisations } from '../components/PatientImmunisations'
import { AssessmentList } from '../components/AssessmentList'
import { CarePlanList } from '../components/CarePlanList'
import { PatientProtocolList } from '../components/PatientProtocolList'
import { PatientResearchStudies } from '../components/PatientResearchStudies'
import { PrescriptionsTabContent } from '../components/PrescriptionsTabContent'
import { ReferralsTabContent } from '../components/ReferralsTabContent'
import { PatientReminders } from '../components/PatientReminders'
import { PatientOverviewQuickNav } from '../components/PatientOverviewQuickNav'
import { PatientFHIRExport } from '../components/PatientFHIRExport'
import { gmsService } from '../services/gmsService'
import { taskService } from '../services/taskService'
import { TaskForm } from '../components/TaskForm'
import { TaskStatusBadge } from '../components/TaskStatusBadge'
import { communicationLogService } from '../services/communicationLogService'
import { CommunicationLogForm } from '../components/CommunicationLogForm'
import { PregnancyList } from '../components/PregnancyList'
import { PregnancyDetail } from '../components/PregnancyDetail'
import { Pregnancy } from '../types/maternity'
import { carePlanService } from '../services/carePlanService'
import { protocolService } from '../services/protocolService'
import { researchStudyService } from '../services/researchStudyService'
import { CarePlanFormData } from '../types/carePlan'
import { CarePlanTemplateSelector } from '../components/CarePlanTemplateSelector'
import { Protocol } from '../types/protocol'
import { ResearchStudy } from '../types/researchStudy'
import { PatientDuplicateSuggestions } from '../components/PatientDuplicateSuggestions'
import { LinkedPatientRecords } from '../components/LinkedPatientRecords'
import { DuplicateSuggestion } from '../types/patient'
import { MaskedField } from '../components/MaskedField'
import { billingService } from '../services/billingService'
import type { BillingAccount, BillableItem, BillingPayment, BillingStatement } from '../types/billing'
import '../styles/navigation-tokens.css'
import './PatientDetail.css'

type TabType =
  | 'overview'
  | 'billing'
  | 'appointments'
  | 'consultations'
  | 'lab-results'
  | 'vitals'
  | 'assessments'
  | 'care-plans'
  | 'protocols'
  | 'research-studies'
  | 'files'
  | 'allergies'
  | 'medications'
  | 'immunisations'
  | 'prescriptions'
  | 'referrals'
  | 'pregnancies'
  | 'tasks'
  | 'communications'
  | 'reminders'
  | 'medical-intel'
  | 'history'
  | 'audit'

export function PatientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { sidebarVisible, hideSidebar, showSidebar } = useSidebar()
  const { setConsultationContext } = useGeminiContext()
  const { openPanel: openLetterPanel } = useLetterWriting()
  const { hasPermission } = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [showConsultationForm, setShowConsultationForm] = useState(false)
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | undefined>()
  const [sidebarWasVisible, setSidebarWasVisible] = useState(false)
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [cancelModalAppointment, setCancelModalAppointment] = useState<Appointment | null>(null)
  const [showFileUpload, setShowFileUpload] = useState(false)
  const [showVitalSignsForm, setShowVitalSignsForm] = useState(false)
  const [showGrowthChartsDialog, setShowGrowthChartsDialog] = useState(false)
  const [isCheckingGms, setIsCheckingGms] = useState(false)
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [showCommunicationLogForm, setShowCommunicationLogForm] = useState(false)
  const [selectedPregnancy, setSelectedPregnancy] = useState<Pregnancy | null>(null)
  const [showCarePlanForm, setShowCarePlanForm] = useState(false)
  const [showCarePlanTemplateSelector, setShowCarePlanTemplateSelector] = useState(false)
  const [showProtocolEligibilityModal, setShowProtocolEligibilityModal] = useState(false)
  const [showResearchStudyEnrollmentModal, setShowResearchStudyEnrollmentModal] = useState(false)

  const { data: patientSummary, isLoading, error } = useQuery({
    queryKey: ['patient-summary', id],
    queryFn: () => patientService.getSummary(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  })

  const patient = patientSummary?.patient
  const consultations = patientSummary?.consultations || []
  const labResults = patientSummary?.labResults || []
  const appointments = patientSummary?.appointments || []
  const allergies = patientSummary?.allergies || []
  const referrals = patientSummary?.referrals || []
  const immunisationsData =
    patientSummary?.immunisations || { items: [], totalCount: 0, hasMore: false }
  const patientFiles = patientSummary?.patientFiles || []
  const patientTasks = patientSummary?.tasks || []
  const communicationLogs = patientSummary?.communicationLogs || []
  const vitalSigns = patientSummary?.vitalSigns || []

  const activePregnancy = patientSummary?.pregnancies?.find((p) => p.status === 'active')

  const isSecondaryRecord = patient ? patient.isPrimary === false || !!patient.primaryPatientId : false
  const canWrite = !isSecondaryRecord
  const primaryRecordId = patient?.primaryPatientId

  // Fetch duplicate suggestions if patient is not linked
  const { data: duplicates = [] } = useQuery<DuplicateSuggestion[]>({
    queryKey: ['patients', 'duplicates', id],
    queryFn: () => patientService.getDuplicates(id!),
    enabled: !!id && !!patient && (patient.isPrimary ?? true) && (!patient.linkedPatientIds || patient.linkedPatientIds.length === 0),
  })

  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })

  const gmsStatus = patientSummary?.gmsStatus ?? null
  const isLoadingGms = isLoading
  const gmsError = error

  const canViewBilling = hasPermission('billing-statements:read')
  const { data: billingAccounts = [] } = useQuery<BillingAccount[]>({
    queryKey: ['billing-accounts', patient?.id],
    queryFn: () => billingService.getAccounts({ patientId: patient!.id }),
    enabled: !!patient?.id && canViewBilling,
  })

  const { data: billingItems = [] } = useQuery<BillableItem[]>({
    queryKey: ['billing-items', patient?.id],
    queryFn: () => billingService.getItems({ patientId: patient!.id }),
    enabled: !!patient?.id && canViewBilling,
  })

  const { data: billingPayments = [] } = useQuery<BillingPayment[]>({
    queryKey: ['billing-payments', patient?.id],
    queryFn: () => billingService.getPayments({ patientId: patient!.id }),
    enabled: !!patient?.id && canViewBilling,
  })

  const { data: billingStatements = [] } = useQuery<BillingStatement[]>({
    queryKey: ['billing-statements', patient?.id],
    queryFn: () => billingService.getStatements({ patientId: patient!.id }),
    enabled: !!patient?.id && canViewBilling,
  })

  const billingAccount = billingAccounts[0]
  const billingCurrency = billingAccount?.currency || 'EUR'
  const billingBalance = billingAccount?.balance ?? 0
  const billingBalanceLabel = new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: billingCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(billingBalance)
  const consultationDateById = new Map(
    consultations.map((consultation) => [consultation.id, consultation.consultationDate])
  )

  const checkGmsMutation = useMutation({
    mutationFn: () => gmsService.checkGMSStatus(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
      setIsCheckingGms(false)
    },
    onError: () => {
      setIsCheckingGms(false)
    },
  })

  const markInactiveMutation = useMutation({
    mutationFn: () => patientService.markInactive(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
    },
  })

  const activateMutation = useMutation({
    mutationFn: () => patientService.activate(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
    },
  })

  const cancelAppointmentMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      appointmentService.cancel(id, reason),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'cancelled' as AppointmentStatus, cancelledAt: new Date().toISOString() }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      setCancelModalAppointment(null)
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to cancel appointment:', error)
      alert('Failed to cancel appointment. Please try again.')
    },
  })

  const handleMarkInactive = async () => {
    if (
      window.confirm(
        `Are you sure you want to mark ${patient?.firstName} ${patient?.lastName} as inactive? This can be reversed.`
      )
    ) {
      try {
        await markInactiveMutation.mutateAsync()
      } catch (error) {
        alert('Failed to mark patient as inactive')
      }
    }
  }

  const handleActivate = async () => {
    if (
      window.confirm(
        `Are you sure you want to activate ${patient?.firstName} ${patient?.lastName}?`
      )
    ) {
      try {
        await activateMutation.mutateAsync()
      } catch (error) {
        alert('Failed to activate patient')
      }
    }
  }

  const handleCancelConfirm = async (reason?: string) => {
    if (cancelModalAppointment) {
      await cancelAppointmentMutation.mutateAsync({ id: cancelModalAppointment.id, reason })
    }
  }

  // Hide sidebar when consultation form opens (Focus mode)
  useEffect(() => {
    if (showConsultationForm) {
      if (sidebarVisible) {
        setSidebarWasVisible(true)
        hideSidebar()
      } else {
        setSidebarWasVisible(false)
      }
    } else {
      if (sidebarWasVisible) {
        showSidebar()
        setSidebarWasVisible(false)
      }
    }
  }, [showConsultationForm])

  // Set Gemini context when on medical-intel tab
  useEffect(() => {
    if (activeTab === 'medical-intel' && patient) {
      const context = buildSanitizedConsultationContext(patient, consultations)
      setConsultationContext(context)
    } else {
      // Clear context when leaving the tab
      setConsultationContext(null)
    }

    return () => {
      // Cleanup: clear context when component unmounts or tab changes
      if (activeTab !== 'medical-intel') {
        setConsultationContext(null)
      }
    }
  }, [activeTab, patient, consultations, setConsultationContext])

  // Handle hash-based navigation (e.g., from care plan detail page)
  // Must be before any early returns to follow Rules of Hooks
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.substring(1) // Remove '#'
      const validTabs: TabType[] = [
        'overview',
        'appointments',
        'consultations',
        'lab-results',
        'vitals',
        'assessments',
        'care-plans',
        'protocols',
        'research-studies',
        'files',
        'allergies',
        'medications',
        'immunisations',
        'prescriptions',
        'referrals',
        'pregnancies',
        'tasks',
        'communications',
        'reminders',
        'medical-intel',
        'history',
        'audit',
      ]
      if (validTabs.includes(hash as TabType)) {
        setActiveTab(hash as TabType)
        // Clear hash after setting tab
        window.history.replaceState(null, '', location.pathname)
      }
    }
  }, [location.hash, location.pathname])

  if (isLoading) return <div className="loading">Loading patient details...</div>
  if (error) return <div className="error">Error loading patient details</div>
  if (!patient) return <div>Patient not found</div>

  const formatAddress = (address?: Patient['address']) => {
    if (!address) return '-'
    const parts = [
      address.street,
      address.city,
      address.state,
      address.postalCode,
      address.country,
    ].filter(Boolean)
    return parts.join(', ') || '-'
  }

  const consultationsCount = consultations?.length || 0
  const latestConsultation = consultations?.length
    ? [...(consultations ?? [])].sort(
        (a, b) =>
          new Date(b.consultationDate).getTime() - new Date(a.consultationDate).getTime()
      )[0]
    : undefined
  const labResultsCount = labResults?.length || 0
  const appointmentsCount = appointments?.length || 0
  const vitalSignsCount = vitalSigns?.length || 0
  const referralsCount = referrals?.length || 0
  const outstandingTasksCount = patientTasks?.filter(
    (task) => task.status !== 'completed' && task.status !== 'resolved' && task.status !== 'cancelled'
  ).length || 0
  // Count files: direct patient files + lab result attachments
  const directFilesCount = patientFiles?.length || 0
  const labResultAttachmentsCount = labResults?.reduce((count, result) => {
    return count + (result.attachments?.length || 0)
  }, 0) || 0
  const filesCount = directFilesCount + labResultAttachmentsCount
  const inReviewLabResultsCount = labResults?.filter((r) => r.reviewStatus === 'in-review').length || 0
  const criticalLabResultsCount = labResults?.filter((r) => r.isCritical).length || 0

  const unitPreference = getUnitPreference()

  // Helper: for each vital type, find the most recent non-null/non-undefined value
  const getLatestVital = <T,>(getValue: (vs: VitalSigns) => T | undefined | null): T | null => {
    if (!vitalSigns || vitalSigns.length === 0) return null

    const sorted = [...vitalSigns].sort((a, b) => {
      const dateA = new Date(a.recordedAt || a.createdAt || '').getTime()
      const dateB = new Date(b.recordedAt || b.createdAt || '').getTime()
      return dateB - dateA
    })

    for (const vs of sorted) {
      const value = getValue(vs)
      if (value !== undefined && value !== null) {
        return value
      }
    }

    return null
  }

  // Build a compact latest vitals summary for the overview card
  let latestVitalsSummary = ''
  if (vitalSigns && vitalSigns.length > 0) {
    const parts: string[] = []

    const latestBloodPressure = getLatestVital((vs) => vs.bloodPressure)
    const latestHeartRate = getLatestVital((vs) => vs.heartRate)
    const latestTemperature = getLatestVital((vs) => vs.temperature)
    const latestRespiratoryRate = getLatestVital((vs) => vs.respiratoryRate)
    const latestOxygenSaturation = getLatestVital((vs) => vs.oxygenSaturation)
    const latestWeight = getLatestVital((vs) => vs.weight)
    const latestHeight = getLatestVital((vs) => vs.height)
    const latestBmi = getLatestVital((vs) => vs.bmi)

    if (latestBloodPressure) {
      parts.push(`BP ${latestBloodPressure.systolic}/${latestBloodPressure.diastolic}`)
    }
    if (latestHeartRate?.value) {
      parts.push(`HR ${latestHeartRate.value}`)
    }
    if (latestTemperature) {
      const displayTemp =
        getDisplayValue(latestTemperature, unitPreference) ?? latestTemperature.value
      const tempUnit = unitPreference === 'imperial' ? 'fahrenheit' : 'celsius'
      parts.push(formatTemperature(displayTemp, tempUnit as 'celsius' | 'fahrenheit'))
    }
    if (latestRespiratoryRate?.value) {
      parts.push(`RR ${latestRespiratoryRate.value}`)
    }
    if (latestOxygenSaturation?.value) {
      parts.push(`SpO₂ ${latestOxygenSaturation.value}%`)
    }
    if (latestWeight) {
      parts.push(
        `Wt ${formatWeight(
          latestWeight.value,
          latestWeight.unit as 'kg' | 'lb' | 'stone',
          unitPreference,
        )}`,
      )
    }
    if (latestHeight) {
      parts.push(
        `Ht ${formatHeight(
          latestHeight.value,
          latestHeight.unit as 'cm' | 'in',
          unitPreference,
        )}`,
      )
    }
    if (typeof latestBmi === 'number' && latestBmi > 0) {
      parts.push(`BMI ${latestBmi.toFixed(1)}`)
    }

    latestVitalsSummary = parts.join(' • ')
  }

  const immunisationsCount = immunisationsData?.totalCount || 0
  const activeAllergyCount =
    allergies?.filter((a) => a.status === 'active' && !a.isNKDA).length || 0
  const hasNkda = allergies?.some((a) => a.isNKDA && a.status === 'active') || false

  const handleCheckGmsStatus = async () => {
    if (!id) return
    setIsCheckingGms(true)
    try {
      await checkGmsMutation.mutateAsync()
    } catch (err) {
      console.error('Failed to check GMS status', err)
    }
  }

  const getGmsStatusConfig = (status: GMSStatus['status']) => {
    const map = {
      eligible: { label: 'Eligible', icon: '✓', className: 'gms-badge-eligible' },
      not_eligible: { label: 'Not eligible', icon: '✗', className: 'gms-badge-not-eligible' },
      unknown: { label: 'Unknown', icon: '?', className: 'gms-badge-unknown' },
      expired: { label: 'Expired', icon: '⚠', className: 'gms-badge-expired' },
    }
    return map[status] || map.unknown
  }

  const getGmsCardTypeLabel = (cardType?: string) => {
    if (!cardType) return 'Unknown'
    const labels: Record<string, string> = {
      medical_card: 'Medical Card',
      gp_visit_card: 'GP Visit Card',
      drug_payment_scheme: 'Drug Payment Scheme',
    }
    return labels[cardType] || cardType
  }

  // Determine which overview sections actually render so we can enable/disable quick nav entries
  const hasAddressSection = !!patient.address
  const hasCoverageSection = !!patient.coverage
  const hasLifestyleSection =
    !!patient.smokingStatus || !!patient.smokingDetails || !!patient.alcoholConsumption
  const hasFamilyRelationshipsSection: boolean =
    !!(patient.nextOfKin && patient.nextOfKin.length > 0) ||
    !!(patient.carers && patient.carers.length > 0)
  const hasNotesSection = !!patient.notes
  const hasMedicalInfoSection =
    !!patient.bloodType ||
    (patient.allergies && patient.allergies.length > 0) ||
    (patient.chronicConditions && patient.chronicConditions.length > 0) ||
    (patient.currentMedications && patient.currentMedications.length > 0) ||
    (patient.pastMedications && patient.pastMedications.length > 0) ||
    !!patient.medicalHistory
  const hasBillingSection = canViewBilling && !!patient?.id

  const overviewAvailableSections: Record<string, boolean> = {
    'demographics': true,
    'health-identifiers': true,
    'contact-information': true,
    'address': hasAddressSection,
    'medical-information': hasMedicalInfoSection,
    'coverage-schemes': hasCoverageSection,
    'lifestyle-factors': hasLifestyleSection,
    'family-relationships': hasFamilyRelationshipsSection,
    'notes': hasNotesSection,
    'billing': hasBillingSection,
    'system-information': true,
  }

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab)

    if (!id) return

    switch (tab) {
      case 'overview':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        break
      case 'consultations':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['consultations', id] })
        break
      case 'lab-results':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['lab-results', 'patient', id] })
        break
      case 'billing':
        queryClient.invalidateQueries({ queryKey: ['billing-accounts', id] })
        queryClient.invalidateQueries({ queryKey: ['billing-items', id] })
        queryClient.invalidateQueries({ queryKey: ['billing-payments', id] })
        queryClient.invalidateQueries({ queryKey: ['billing-statements', id] })
        break
      case 'vitals':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['vital-signs', 'patient', id] })
        break
      case 'files':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['patient-files', 'patient', id] })
        break
      case 'allergies':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['allergies', id] })
        break
      case 'medications':
        queryClient.invalidateQueries({ queryKey: ['patient-medications', id] })
        break
      case 'referrals':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['referrals'] })
        queryClient.invalidateQueries({ queryKey: ['referrals', id] })
        break
      case 'tasks':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['tasks', 'patient', id] })
        break
      case 'communications':
        queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
        queryClient.invalidateQueries({ queryKey: ['communication-logs', 'patient', id] })
        break
      case 'care-plans':
        queryClient.invalidateQueries({ queryKey: ['care-plans', id] })
        break
      case 'protocols':
        queryClient.invalidateQueries({ queryKey: ['patient-protocols', id] })
        break
      case 'research-studies':
        queryClient.invalidateQueries({ queryKey: ['patient-research-studies', id] })
        break
      case 'history':
        queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', id] })
        break
      case 'audit':
        queryClient.invalidateQueries({ queryKey: ['patient-audit', id] })
        break
      case 'medical-intel':
        // Lab results already invalidated via lab-results; no extra query key here
        break
      default:
        break
    }
  }

  // Prepare blood test time series for Medical Intelligence tab
  const bloodResults = (labResults || []).filter((r) => r.type === 'blood')
  const bloodSeriesMap: Record<
    string,
    {
      testName: string
      points: { date: Date; value: number; flag?: string }[]
    }
  > = {}

  bloodResults.forEach((result) => {
    const reportedDateTime = new Date(result.reportedDate + 'T' + (result.reportedTime || '00:00'))
    result.panels.forEach((panel) => {
      panel.tests.forEach((test) => {
        const numericValue =
          typeof test.value === 'number' ? test.value : Number(String(test.value).replace(/[^0-9.\-]/g, ''))
        if (Number.isNaN(numericValue)) return
        const key = `${test.testCode || test.testName}`
        if (!bloodSeriesMap[key]) {
          bloodSeriesMap[key] = {
            testName: test.testName || test.testCode || 'Test',
            points: [],
          }
        }
        bloodSeriesMap[key].points.push({
          date: reportedDateTime,
          value: numericValue,
          flag: test.flag,
        })
      })
    })
  })

  const bloodSeries = Object.values(bloodSeriesMap).map((series) => {
    const sorted = [...series.points].sort((a, b) => a.date.getTime() - b.date.getTime())
    const min = Math.min(...sorted.map((p) => p.value))
    const max = Math.max(...sorted.map((p) => p.value))
    return { ...series, points: sorted, min, max }
  })

  return (
    <div className="patient-detail">
      <div className="patient-detail-header">
        <div>
          <h1>
            {formatPatientName(patient as any)}
          </h1>
          <p className="patient-subtitle">
            {patient.patientNumber || patient.id.slice(0, 8)} • DOB: {new Date(
              patient.dateOfBirth,
            ).toLocaleDateString()}
            {' • '}
            {patient.gender}
            {patient.ppsNumber && (
              <>
                {' • '}
                PPS: {patient.ppsNumber}
              </>
            )}
            {patient.practiceRegistration?.registeredGpName && (
              <>
                {' • '}
                Registered GP: {patient.practiceRegistration.registeredGpName}
              </>
            )}
          </p>
          {isSecondaryRecord && (
            <div className="patient-readonly-banner">
              This linked record is read-only. Update the primary record instead.
              {primaryRecordId && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => navigate(`/patients/${primaryRecordId}`)}
                  >
                    View primary record
                  </button>
                </>
              )}
            </div>
          )}
          <div className="gms-header-row">
            {gmsStatus && !isLoadingGms && !gmsError ? (
              <>
                {(() => {
                  const cfg = getGmsStatusConfig(gmsStatus.status)
                  return (
                    <span className={`gms-badge ${cfg.className}`}>
                      <span className="gms-badge-icon" aria-hidden="true">
                        {cfg.icon}
                      </span>
                      {cfg.label}
                    </span>
                  )
                })()}
                {gmsStatus.gmsNumber && (
                  <span className="gms-header-field">GMS: {gmsStatus.gmsNumber}</span>
                )}
                {gmsStatus.cardType && (
                  <span className="gms-header-field">
                    {getGmsCardTypeLabel(gmsStatus.cardType)}
                  </span>
                )}
                {gmsStatus.schemeType && (
                  <span className="gms-header-field">{gmsStatus.schemeType}</span>
                )}
                {gmsStatus.doctorNumber && (
                  <span className="gms-header-field">Doctor: {gmsStatus.doctorNumber}</span>
                )}
                {gmsStatus.lastChecked && (
                  <span className="gms-header-field muted">
                    Last checked: {formatDate(gmsStatus.lastChecked)}
                  </span>
                )}
              </>
            ) : (
              <span className="gms-header-field muted">
                {gmsError ? 'GMS status unavailable' : 'No GMS status recorded'}
              </span>
            )}
            <button
              type="button"
              className="btn-gms-refresh"
              onClick={handleCheckGmsStatus}
              disabled={isCheckingGms || !id}
              aria-label="Refresh GMS eligibility status"
            >
              {isCheckingGms ? 'Checking…' : 'Check GMS'}
            </button>
          </div>
        </div>
        <div className="patient-actions">
          <div className="patient-actions-row">
            <button
              className="btn-primary"
              onClick={() => {
                setSelectedConsultation(undefined)
                setShowConsultationForm(true)
              }}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Secondary records are read-only'}
            >
              Start Consultation
            </button>
            <button
              className="btn-primary"
              onClick={() => setShowAppointmentModal(true)}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Secondary records are read-only'}
            >
              Book Appointment
            </button>
            <button
              className="btn-primary"
              onClick={() => id && openLetterPanel({ patientId: id })}
              disabled={!canWrite || !id}
              title={canWrite ? undefined : 'Secondary records are read-only'}
            >
              Write letter
            </button>
          </div>
          <div className="patient-actions-row">
            {canWrite && (
              <button
                className="btn-secondary"
                onClick={() => navigate(`/patients/${id}/edit`)}
              >
                Edit
              </button>
            )}
            {canWrite && (
              <button
                className="btn-secondary"
                onClick={() => latestConsultation && navigate(`/consultations/${latestConsultation.id}/stc-signing`)}
                disabled={!latestConsultation}
                title={
                  latestConsultation
                    ? 'Open STC / MICS signing for the most recent consultation'
                    : 'Start a consultation first to complete an STC Patient Declaration'
                }
              >
                STC Patient Declaration
              </button>
            )}
            <PatientFHIRExport
              patientId={id!}
            />
            {patient?.isActive ? (
              <button
                className="btn-danger"
                onClick={handleMarkInactive}
                disabled={markInactiveMutation.isPending || !canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                {markInactiveMutation.isPending ? 'Marking Inactive...' : 'Mark Inactive'}
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleActivate}
                disabled={activateMutation.isPending || !canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                {activateMutation.isPending ? 'Activating...' : 'Activate'}
              </button>
            )}
          </div>
        </div>
      </div>

      {activePregnancy && (
        <div
          className="active-pregnancy-edd-alert"
          role="alert"
          aria-live="polite"
        >
          <span className="active-pregnancy-edd-alert-icon" aria-hidden="true">
            🤰
          </span>
          <div className="active-pregnancy-edd-alert-content">
            <strong>Active pregnancy</strong>
            <span className="active-pregnancy-edd-alert-edd">
              Estimated Due Date: {formatDate(activePregnancy.estimatedDueDate)}
            </span>
            <span className="active-pregnancy-edd-alert-gestation">
              Gestation: {calculateGestationalAge(activePregnancy.lastMenstrualPeriod).display}
            </span>
          </div>
          <button
            type="button"
            className="active-pregnancy-edd-alert-link"
            onClick={() => {
              setSelectedPregnancy(activePregnancy)
              setActiveTab('pregnancies')
            }}
          >
            View pregnancy
          </button>
        </div>
      )}

      {/* Preventive Services Alerts */}
      <PreventiveServicesAlerts
        patient={patient}
        hasActivePregnancy={!!activePregnancy}
        onServiceClick={(service) => {
          if (service.actionUrl) {
            // Handle tab-based navigation (e.g., #immunisations -> 'immunisations' tab)
            if (service.actionUrl === '#immunisations') {
              handleTabChange('immunisations')
            } else if (service.actionUrl.includes('/edit')) {
              if (!canWrite) {
                return
              }
              navigate(service.actionUrl)
            } else {
              navigate(service.actionUrl)
            }
          }
        }}
      />

      {/* Summary Cards */}
      <div className="patient-summary-cards">
        <div className="summary-card" onClick={() => setActiveTab('consultations')}>
          <div className="summary-card-icon consultations">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="summary-card-content">
            <div className="summary-card-value">{consultationsCount}</div>
            <div className="summary-card-label">Consultations</div>
          </div>
        </div>
        <div className="summary-card" onClick={() => setActiveTab('lab-results')}>
          <div className="summary-card-icon lab-results">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="summary-card-content">
            <div className="summary-card-value">{labResultsCount}</div>
            <div className="summary-card-label">Lab Results</div>
            {inReviewLabResultsCount > 0 && (
              <div className="summary-card-badge in-review">{inReviewLabResultsCount} in review</div>
            )}
            {criticalLabResultsCount > 0 && (
              <div className="summary-card-badge critical">{criticalLabResultsCount} critical</div>
            )}
          </div>
        </div>
        <div className="summary-card" onClick={() => setActiveTab('vitals')}>
          <div className="summary-card-icon vitals">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12L8 7L13 12L21 4M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="summary-card-content">
            <div className="summary-card-value">{vitalSignsCount}</div>
            <div className="summary-card-label">Vital Signs</div>
            {latestVitalsSummary && (
              <div className="summary-card-subtext">{latestVitalsSummary}</div>
            )}
          </div>
        </div>
        <div className="summary-card" onClick={() => handleTabChange('appointments')}>
          <div className="summary-card-icon appointments">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="summary-card-content">
            <div className="summary-card-value">{appointmentsCount}</div>
            <div className="summary-card-label">Appointments</div>
          </div>
        </div>
        <div className="summary-card" onClick={() => handleTabChange('immunisations')}>
          <div className="summary-card-icon immunisations">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="summary-card-content">
            <div className="summary-card-value">{immunisationsCount}</div>
            <div className="summary-card-label">Immunisations</div>
          </div>
        </div>
      </div>

      {/* Patient Detail Container with Sidebar */}
      <div className="patient-detail-container">
        {/* Vertical Sidebar Menu */}
        <nav className="patient-menu-sidebar" role="navigation" aria-label="Patient detail navigation">
          <ul className="patient-menu-list">
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => handleTabChange('overview')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </span>
                <span className="patient-menu-label">Overview</span>
              </button>
            </li>
            {canViewBilling && (
              <li>
                <button
                  className={`patient-menu-item ${activeTab === 'billing' ? 'active' : ''}`}
                  onClick={() => handleTabChange('billing')}
                >
                  <span className="patient-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="16" rx="2" ry="2"></rect>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                      <line x1="8" y1="15" x2="10" y2="15"></line>
                      <line x1="12" y1="15" x2="16" y2="15"></line>
                    </svg>
                  </span>
                  <span className="patient-menu-label">Billing</span>
                  {billingAccount && (
                    <span className={`patient-menu-badge ${billingBalance > 0 ? 'warning' : ''}`}>
                      {billingBalanceLabel}
                    </span>
                  )}
                </button>
              </li>
            )}
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'appointments' ? 'active' : ''}`}
                onClick={() => handleTabChange('appointments')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </span>
                <span className="patient-menu-label">Appointments</span>
                {appointmentsCount > 0 && <span className="patient-menu-badge">{appointmentsCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'consultations' ? 'active' : ''}`}
                onClick={() => handleTabChange('consultations')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Consultations</span>
                {consultationsCount > 0 && <span className="patient-menu-badge">{consultationsCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'lab-results' ? 'active' : ''}`}
                onClick={() => handleTabChange('lab-results')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="6" x2="12" y2="12"></line>
                    <line x1="16" y1="10" x2="12" y2="12"></line>
                  </svg>
                </span>
                <span className="patient-menu-label">Lab Results</span>
                {labResultsCount > 0 && <span className="patient-menu-badge">{labResultsCount}</span>}
                {inReviewLabResultsCount > 0 && <span className="patient-menu-badge warning">{inReviewLabResultsCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'vitals' ? 'active' : ''}`}
                onClick={() => handleTabChange('vitals')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12L8 7L13 12L21 4M3 21H21"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Vitals</span>
                {vitalSignsCount > 0 && <span className="patient-menu-badge">{vitalSignsCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'files' ? 'active' : ''}`}
                onClick={() => handleTabChange('files')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Files</span>
                {filesCount > 0 && <span className="patient-menu-badge">{filesCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item allergies-menu-item ${activeTab === 'allergies' ? 'active' : ''}`}
                onClick={() => handleTabChange('allergies')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z"></path>
                    <path d="M2 17L12 22L22 17"></path>
                    <path d="M2 12L12 17L22 12"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Allergies</span>
                {(activeAllergyCount > 0 || hasNkda) && (
                  <span className="patient-menu-badge">
                    {activeAllergyCount > 0 ? activeAllergyCount : 'NKDA'}
                  </span>
                )}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'medications' ? 'active' : ''}`}
                onClick={() => handleTabChange('medications')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="15" y1="3" x2="15" y2="21"></line>
                  </svg>
                </span>
                <span className="patient-menu-label">Medications</span>
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'immunisations' ? 'active' : ''}`}
                onClick={() => handleTabChange('immunisations')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z"></path>
                    <path d="M2 17L12 22L22 17"></path>
                    <path d="M2 12L12 17L22 12"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Immunisations</span>
                {immunisationsCount > 0 && <span className="patient-menu-badge">{immunisationsCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'prescriptions' ? 'active' : ''}`}
                onClick={() => handleTabChange('prescriptions')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                </span>
                <span className="patient-menu-label">Prescriptions</span>
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'referrals' ? 'active' : ''}`}
                onClick={() => handleTabChange('referrals')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </span>
                <span className="patient-menu-label">Referrals</span>
                {referralsCount > 0 && <span className="patient-menu-badge">{referralsCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'pregnancies' ? 'active' : ''}`}
                onClick={() => handleTabChange('pregnancies')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Pregnancies</span>
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'tasks' ? 'active' : ''}`}
                onClick={() => handleTabChange('tasks')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                </span>
                <span className="patient-menu-label">Tasks</span>
                {outstandingTasksCount > 0 && <span className="patient-menu-badge warning">{outstandingTasksCount}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'communications' ? 'active' : ''}`}
                onClick={() => handleTabChange('communications')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Communications</span>
                {communicationLogs.length > 0 && <span className="patient-menu-badge">{communicationLogs.length}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'reminders' ? 'active' : ''}`}
                onClick={() => handleTabChange('reminders')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Reminders</span>
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'medical-intel' ? 'active' : ''}`}
                onClick={() => handleTabChange('medical-intel')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z"></path>
                    <path d="M2 17L12 22L22 17"></path>
                    <path d="M2 12L12 17L22 12"></path>
                  </svg>
                </span>
                <span className="patient-menu-label">Medical Intelligence</span>
                {bloodSeries.length > 0 && <span className="patient-menu-badge">{bloodSeries.length}</span>}
              </button>
            </li>
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'care-plans' ? 'active' : ''}`}
                onClick={() => handleTabChange('care-plans')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </span>
                <span className="patient-menu-label">Care Plans</span>
              </button>
            </li>
            {hasPermission('protocols:read') && (
              <li>
                <button
                  className={`patient-menu-item ${activeTab === 'protocols' ? 'active' : ''}`}
                  onClick={() => handleTabChange('protocols')}
                >
                  <span className="patient-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <path d="M9 12l2 2 4-4"></path>
                    </svg>
                  </span>
                  <span className="patient-menu-label">Protocols</span>
                </button>
              </li>
            )}
            {hasPermission('research-studies:read') && (
              <li>
                <button
                  className={`patient-menu-item ${activeTab === 'research-studies' ? 'active' : ''}`}
                  onClick={() => handleTabChange('research-studies')}
                >
                  <span className="patient-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 2v20M5 6h8M5 10h8M5 14h8M5 18h8M15 2v20M19 6h-8M19 10h-8M19 14h-8M19 18h-8"></path>
                    </svg>
                  </span>
                  <span className="patient-menu-label">Research Studies</span>
                </button>
              </li>
            )}
            <li>
              <button
                className={`patient-menu-item ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => handleTabChange('history')}
              >
                <span className="patient-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </span>
                <span className="patient-menu-label">Clinical History</span>
              </button>
            </li>
            {hasPermission('audit:read') && (
              <li>
                <button
                  className={`patient-menu-item ${activeTab === 'audit' ? 'active' : ''}`}
                  onClick={() => handleTabChange('audit')}
                >
                  <span className="patient-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      <path d="M9 12l2 2 4-4"></path>
                    </svg>
                  </span>
                  <span className="patient-menu-label">Audit History</span>
                </button>
              </li>
            )}
          </ul>
        </nav>

        {/* Tab Content */}
        <div className="patient-tab-content">
        {activeTab === 'overview' && (
          <>
          <div className="overview-content">
            {/* Linked Records Section - Show if patient has linked records or is linked */}
            {patient && (
              <>
                <LinkedPatientRecords
                  patient={patient}
                  onUpdate={() => {
                    queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
                  }}
                />
                {/* Duplicate Suggestions - Only show if not already linked */}
                {((patient.isPrimary ?? true) && (!patient.linkedPatientIds || patient.linkedPatientIds.length === 0)) && (
                  <PatientDuplicateSuggestions
                    patientId={id!}
                    duplicates={duplicates}
                    onLink={() => {
                      queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
                      queryClient.invalidateQueries({ queryKey: ['patients', 'duplicates', id] })
                    }}
                  />
                )}
              </>
            )}

            <div className="info-section" id="demographics">
              <h2>Demographics</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Patient Number</label>
                  <p>{patient.patientNumber || patient.id.slice(0, 8)}</p>
                </div>
                <div className="info-item">
                  <label>Full Name</label>
                  <p>
                    {patient.firstName} {patient.middleName} {patient.lastName}
                  </p>
                </div>
                <div className="info-item">
                  <label>Date of Birth</label>
                  <p>{new Date(patient.dateOfBirth).toLocaleDateString()}</p>
                </div>
                <div className="info-item">
                  <label>Gender</label>
                  <p>{patient.gender}</p>
                </div>
                {patient.maritalStatus && (
                  <div className="info-item">
                    <label>Marital Status</label>
                    <p>{patient.maritalStatus}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="info-section" id="health-identifiers">
              <h2>Health Identifiers</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Patient Number</label>
                  <p>{patient.patientNumber || patient.id.slice(0, 8)}</p>
                </div>
                {patient.ppsNumber && (
                  <div className="info-item">
                    <label>PPS Number</label>
                    <p>{patient.ppsNumber}</p>
                  </div>
                )}
                {patient.gmsNumber && (
                  <div className="info-item">
                    <label>GMS Number</label>
                    <p>{patient.gmsNumber}</p>
                  </div>
                )}
                {patient.identifiersExtended?.ihiNumber && (
                  <div className="info-item">
                    <label>IHI Number</label>
                    <p>{patient.identifiersExtended.ihiNumber}</p>
                  </div>
                )}
              </div>
              {patient.identifiersExtended?.externalIds &&
                patient.identifiersExtended.externalIds.length > 0 && (
                  <div className="info-grid">
                    <div className="info-item full-width">
                      <label>External Identifiers</label>
                      <ul className="pill-list muted">
                        {patient.identifiersExtended.externalIds.map((idRef, idx) => (
                          <li key={idx} className="pill">
                            {idRef.system}: {idRef.identifier}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
            </div>

            <div className="info-section" id="contact-information">
              <h2>Contact Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Phone</label>
                  <p>{patient.phone}</p>
                </div>
                {patient.alternatePhone && (
                  <div className="info-item">
                    <label>Alternate Phone</label>
                    <p>{patient.alternatePhone}</p>
                  </div>
                )}
                {patient.email && (
                  <div className="info-item">
                    <label>Email</label>
                    <p>{patient.email}</p>
                  </div>
                )}
              </div>
            </div>

            {patient.address && (
              <div className="info-section" id="address">
                <h2>Address</h2>
                <div className="info-grid">
                  <div className="info-item full-width">
                    <label>Address</label>
                    <p>{formatAddress(patient.address)}</p>
                  </div>
                </div>
              </div>
            )}

            {patient.emergencyContact && (
              <div className="info-section">
                <h2>Emergency Contact</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name</label>
                    <p>{patient.emergencyContact.name}</p>
                  </div>
                  <div className="info-item">
                    <label>Relationship</label>
                    <p>{patient.emergencyContact.relationship}</p>
                  </div>
                  <div className="info-item">
                    <label>Phone</label>
                    <p>{patient.emergencyContact.phone}</p>
                  </div>
                  {patient.emergencyContact.email && (
                    <div className="info-item">
                      <label>Email</label>
                      <p>{patient.emergencyContact.email}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="info-section" id="medical-information">
              <h2>Medical Information</h2>
              <div className="info-grid">
                {patient.bloodType && (
                  <div className="info-item">
                    <label>Blood Type</label>
                    <p>{patient.bloodType}</p>
                  </div>
                )}
                {patient.allergies && patient.allergies.length > 0 && (
                  <div className="info-item full-width">
                    <label>Allergies</label>
                    <p>{patient.allergies.join(', ')}</p>
                  </div>
                )}
                {patient.chronicConditions && patient.chronicConditions.length > 0 && (
                  <div className="info-item full-width">
                    <label>Chronic Conditions</label>
                    <MaskedField
                      field="conditions"
                      value={patient.chronicConditions}
                      patientId={patient.id}
                      confidentialityLevel="restricted"
                    >
                      {(canAccess, maskedConditions, requestEmergencyAccess) => {
                        if (!canAccess) {
                          return (
                            <div>
                              <p className="text-muted">Access restricted</p>
                              {requestEmergencyAccess && (
                                <button
                                  onClick={requestEmergencyAccess}
                                  className="btn-link btn-small"
                                  style={{ marginTop: '0.5rem' }}
                                >
                                  Request Emergency Access
                                </button>
                              )}
                            </div>
                          )
                        }
                        return <p>{Array.isArray(maskedConditions) ? maskedConditions.join(', ') : '[Restricted]'}</p>
                      }}
                    </MaskedField>
                  </div>
                )}
                {patient.currentMedications && patient.currentMedications.length > 0 && (
                  <div className="info-item full-width">
                    <label>Current Medications</label>
                    <MaskedField
                      field="medications"
                      value={patient.currentMedications}
                      patientId={patient.id}
                      confidentialityLevel="restricted"
                    >
                      {(canAccess, maskedMedications, requestEmergencyAccess) => {
                        if (!canAccess) {
                          return (
                            <div>
                              <p className="text-muted">Access restricted</p>
                              {requestEmergencyAccess && (
                                <button
                                  onClick={requestEmergencyAccess}
                                  className="btn-link btn-small"
                                  style={{ marginTop: '0.5rem' }}
                                >
                                  Request Emergency Access
                                </button>
                              )}
                            </div>
                          )
                        }
                        if (Array.isArray(maskedMedications)) {
                          return (
                            <ul className="pill-list">
                              {maskedMedications.map((m: any, idx: number) => (
                                <li key={idx} className="pill">
                                  {typeof m === 'string' ? m : m.name || m}
                                </li>
                              ))}
                            </ul>
                          )
                        }
                        return <p className="text-muted">[Restricted]</p>
                      }}
                    </MaskedField>
                  </div>
                )}
                {patient.pastMedications && patient.pastMedications.length > 0 && (
                  <div className="info-item full-width">
                    <label>Past Medications</label>
                    <MaskedField
                      field="medications"
                      value={patient.pastMedications}
                      patientId={patient.id}
                      confidentialityLevel="restricted"
                    >
                      {(canAccess, maskedMedications) => {
                        if (!canAccess) {
                          return <p className="text-muted">Access restricted</p>
                        }
                        if (Array.isArray(maskedMedications)) {
                          return (
                            <ul className="pill-list muted">
                              {maskedMedications.map((m: any, idx: number) => (
                                <li key={idx} className="pill">
                                  {typeof m === 'string' ? m : m.name || m}
                                </li>
                              ))}
                            </ul>
                          )
                        }
                        return <p className="text-muted">[Restricted]</p>
                      }}
                    </MaskedField>
                  </div>
                )}
                {patient.medicalHistory && (
                  <div className="info-item full-width">
                    <label>Medical History</label>
                    <p className="multiline">{patient.medicalHistory}</p>
                  </div>
                )}
              </div>
            </div>

            {patient.coverage && (
              <div className="info-section" id="coverage-schemes">
                <h2>Coverage & Schemes</h2>
                <div className="info-grid">
                  {patient.coverage.patientType && (
                    <div className="info-item">
                      <label>Patient Type</label>
                      <p>{patient.coverage.patientType}</p>
                    </div>
                  )}
                  {patient.coverage.gms?.number && (
                    <div className="info-item">
                      <label>GMS Number</label>
                      <p>{patient.coverage.gms.number}</p>
                    </div>
                  )}
                  {patient.coverage.doctorVisitCard?.number && (
                    <div className="info-item">
                      <label>Doctor Visit Card Number</label>
                      <p>{patient.coverage.doctorVisitCard.number}</p>
                    </div>
                  )}
                  {patient.coverage.dps?.number && (
                    <div className="info-item">
                      <label>DPS Number</label>
                      <p>{patient.coverage.dps.number}</p>
                    </div>
                  )}
                  {patient.coverage.privateInsurance && (
                    <div className="info-item full-width">
                      <label>Private Insurance</label>
                      <p>
                        {patient.coverage.privateInsurance.company} •{' '}
                        {patient.coverage.privateInsurance.membershipNumber}{' '}
                        {patient.coverage.privateInsurance.coverType &&
                          `(${patient.coverage.privateInsurance.coverType})`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(patient.socioCultural || patient.communicationSupport) && (
              <div className="info-section">
                <h2>Socio-cultural & Communication</h2>
                <div className="info-grid">
                  {patient.socioCultural?.occupation && (
                    <div className="info-item">
                      <label>Occupation</label>
                      <p>{patient.socioCultural.occupation}</p>
                    </div>
                  )}
                  {patient.socioCultural?.religion && (
                    <div className="info-item">
                      <label>Religion</label>
                      <p>{patient.socioCultural.religion}</p>
                    </div>
                  )}
                  {patient.socioCultural?.ethnicity && (
                    <div className="info-item">
                      <label>Ethnicity</label>
                      <p>{patient.socioCultural.ethnicity}</p>
                    </div>
                  )}
                  {patient.communicationSupport?.preferredLanguage && (
                    <div className="info-item">
                      <label>Preferred Language</label>
                      <p>{patient.communicationSupport.preferredLanguage}</p>
                    </div>
                  )}
                  {patient.communicationSupport?.interpreterRequired && (
                    <div className="info-item">
                      <label>Interpreter Required</label>
                      <p>Yes</p>
                    </div>
                  )}
                </div>
                {(patient.communicationSupport?.transportNeeds ||
                  patient.communicationSupport?.advocacyNeeds) && (
                  <div className="info-grid">
                    {patient.communicationSupport.transportNeeds && (
                      <div className="info-item full-width">
                        <label>Transport Needs</label>
                        <p className="multiline">
                          {patient.communicationSupport.transportNeeds}
                        </p>
                      </div>
                    )}
                    {patient.communicationSupport.advocacyNeeds && (
                      <div className="info-item full-width">
                        <label>Advocacy Needs</label>
                        <p className="multiline">
                          {patient.communicationSupport.advocacyNeeds}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {patient.practiceRegistration && (
              <div className="info-section">
                <h2>Practice Registration</h2>
                <div className="info-grid">
                  {patient.practiceRegistration.registeredGpName && (
                    <div className="info-item">
                      <label>Registered GP</label>
                      <p>{patient.practiceRegistration.registeredGpName}</p>
                    </div>
                  )}
                  {patient.practiceRegistration.usualGpName && (
                    <div className="info-item">
                      <label>Usual GP</label>
                      <p>{patient.practiceRegistration.usualGpName}</p>
                    </div>
                  )}
                  {patient.practiceRegistration.branchName && (
                    <div className="info-item">
                      <label>Branch Surgery</label>
                      <p>{patient.practiceRegistration.branchName}</p>
                    </div>
                  )}
                </div>
                <div className="info-grid">
                  {patient.practiceRegistration.registrationDate && (
                    <div className="info-item">
                      <label>Registration Date</label>
                      <p>
                        {new Date(
                          patient.practiceRegistration.registrationDate
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {patient.practiceRegistration.deregistrationDate && (
                    <div className="info-item">
                      <label>Deregistration Date</label>
                      <p>
                        {new Date(
                          patient.practiceRegistration.deregistrationDate
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
                {patient.practiceRegistration.deregistrationReason && (
                  <div className="info-grid">
                    <div className="info-item full-width">
                      <label>Reason for Deregistration</label>
                      <p className="multiline">
                        {patient.practiceRegistration.deregistrationReason}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {patient.endOfLife?.dateOfDeath && (
              <div className="info-section">
                <h2>End of Life</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Date of Death</label>
                    <p>{new Date(patient.endOfLife.dateOfDeath).toLocaleDateString()}</p>
                  </div>
                  {patient.endOfLife.causesOfDeath && (
                    <div className="info-item full-width">
                      <label>Cause(s) of Death</label>
                      <p className="multiline">{patient.endOfLife.causesOfDeath}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {patient.nextOfKin && patient.nextOfKin.length > 0 && (
              <div className="info-section" id="family-relationships">
                <h2>Next of Kin</h2>
                <div className="info-grid">
                  {patient.nextOfKin.map((nok, idx) => (
                    <div key={idx} className="info-item full-width">
                      <label>{nok.relationshipToPatient || 'Next of Kin'}</label>
                      <p>
                        {nok.title && `${nok.title} `}{nok.firstName} {nok.familyName}
                      </p>
                      <p>
                        {[nok.mobilePhone, nok.homePhone, nok.email].filter(Boolean).join(' • ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {patient.carers && patient.carers.length > 0 && (
              <div className="info-section">
                <h2>Carers</h2>
                <div className="info-grid">
                  {patient.carers.map((carer, idx) => (
                    <div key={idx} className="info-item full-width">
                      <label>{carer.relationshipToPatient || 'Carer'}</label>
                      <p>
                        {carer.title && `${carer.title} `}{carer.firstName} {carer.familyName}
                      </p>
                      <p>
                        {[carer.mobilePhone, carer.homePhone, carer.email].filter(Boolean).join(' • ')}
                      </p>
                      {carer.consentRecordedAt && (
                        <p className="muted">
                          Consent recorded:{' '}
                          {new Date(carer.consentRecordedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(patient.smokingStatus || patient.smokingDetails || patient.alcoholConsumption) && (
              <div className="info-section" id="lifestyle-factors">
                <h2>Lifestyle Factors</h2>
                <div className="lifestyle-grid">
                  {/* Smoking Status */}
                  {(patient.smokingStatus || patient.smokingDetails) && (
                    <div className="lifestyle-card">
                      <div className="lifestyle-card-header">
                        <label>Smoking Status</label>
                        {patient.smokingStatusLastRecorded && (
                          <span className="lifestyle-timestamp">
                            {new Date(patient.smokingStatusLastRecorded).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div className="lifestyle-card-body">
                        <div className="lifestyle-status-row">
                          <span className={`lifestyle-badge smoking-${patient.smokingStatus || patient.smokingDetails?.status}`}>
                            {patient.smokingStatus === 'never' && 'Never Smoker'}
                            {patient.smokingStatus === 'ex-smoker' && 'Ex-Smoker'}
                            {patient.smokingStatus === 'current-daily' && 'Current Smoker (Daily)'}
                            {patient.smokingStatus === 'current-occasional' && 'Current Smoker (Occasional)'}
                            {patient.smokingStatus === 'e-cigarette' && 'E-cigarette User'}
                            {!patient.smokingStatus && patient.smokingDetails?.status && 
                              (patient.smokingDetails.status === 'never' ? 'Never Smoker' :
                               patient.smokingDetails.status === 'ex-smoker' ? 'Ex-Smoker' :
                               patient.smokingDetails.status === 'current-daily' ? 'Current Smoker (Daily)' :
                               patient.smokingDetails.status === 'current-occasional' ? 'Current Smoker (Occasional)' :
                               patient.smokingDetails.status === 'e-cigarette' ? 'E-cigarette User' : 'Unknown')
                            }
                          </span>
                        </div>
                        {(patient.smokingDetails?.amountPerDay || patient.smokingDetails?.yearsSmoking) && (
                          <div className="lifestyle-details-inline">
                            {patient.smokingDetails.amountPerDay && (
                              <span className="lifestyle-detail-inline">
                                <strong>{patient.smokingDetails.amountPerDay}</strong> cigarettes/day
                              </span>
                            )}
                            {patient.smokingDetails.yearsSmoking && (
                              <span className="lifestyle-detail-inline">
                                for <strong>{patient.smokingDetails.yearsSmoking} years</strong>
                              </span>
                            )}
                          </div>
                        )}
                        {patient.smokingDetails?.description && (
                          <p className="lifestyle-text">{patient.smokingDetails.description}</p>
                        )}
                        {patient.smokingDetails?.cessationDetails && (
                          <p className="lifestyle-text">
                            <strong>Cessation:</strong> {patient.smokingDetails.cessationDetails}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Alcohol Consumption */}
                  {patient.alcoholConsumption && (
                    <div className="lifestyle-card">
                      <div className="lifestyle-card-header">
                        <label>Alcohol Consumption</label>
                      </div>
                      <div className="lifestyle-card-body">
                        <div className="lifestyle-status-row">
                          <span className={`lifestyle-badge alcohol-${patient.alcoholConsumption.status}`}>
                            {patient.alcoholConsumption.status === 'non-drinker' && 'Non-Drinker'}
                            {patient.alcoholConsumption.status === 'ex-drinker' && 'Ex-Drinker'}
                            {patient.alcoholConsumption.status === 'current-drinker' && 'Current Drinker'}
                          </span>
                          {patient.alcoholConsumption.bingeDrinking && (
                            <span className="lifestyle-badge warning-badge">
                              ⚠️ Binge Drinking
                            </span>
                          )}
                        </div>
                        {patient.alcoholConsumption.status === 'current-drinker' && (
                          <div className="lifestyle-details-inline">
                            {patient.alcoholConsumption.frequency && (
                              <span className="lifestyle-detail-inline">
                                <strong>Frequency:</strong> {patient.alcoholConsumption.frequency}
                              </span>
                            )}
                            {patient.alcoholConsumption.quantityPerOccasion && (
                              <span className="lifestyle-detail-inline">
                                <strong>Quantity:</strong> {patient.alcoholConsumption.quantityPerOccasion}
                              </span>
                            )}
                            {patient.alcoholConsumption.type && (
                              <span className="lifestyle-detail-inline">
                                <strong>Type:</strong> {patient.alcoholConsumption.type}
                              </span>
                            )}
                            {patient.alcoholConsumption.bingeFrequency && (
                              <span className="lifestyle-detail-inline warning-text">
                                <strong>Binge:</strong> {patient.alcoholConsumption.bingeFrequency}
                              </span>
                            )}
                          </div>
                        )}
                        {(patient.alcoholConsumption.description && 
                          (!patient.alcoholConsumption.frequency || 
                           patient.alcoholConsumption.description !== 
                           `Patient drinks alcohol ${patient.alcoholConsumption.frequency}, typically consumes ${patient.alcoholConsumption.quantityPerOccasion}. Prefers ${patient.alcoholConsumption.type}.`)) && (
                          <p className="lifestyle-text">{patient.alcoholConsumption.description}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {patient.insurance && (
              <div className="info-section">
                <h2>Insurance</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Provider</label>
                    <p>{patient.insurance.provider}</p>
                  </div>
                  <div className="info-item">
                    <label>Policy Number</label>
                    <p>{patient.insurance.policyNumber}</p>
                  </div>
                  {patient.insurance.groupNumber && (
                    <div className="info-item">
                      <label>Group Number</label>
                      <p>{patient.insurance.groupNumber}</p>
                    </div>
                  )}
                  {patient.isDependent && (
                    <div className="info-item">
                      <label>Dependent Status</label>
                      <p>
                        <span className="badge dependent">Dependent</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(patient.familyId || patient.guardianId) && (
              <div className="info-section">
                <h2>Family & Relationships</h2>
                <div className="info-grid">
                  {patient.familyId && (
                    <div className="info-item">
                      <label>Family</label>
                      <p>
                        <button
                          className="btn-link"
                          onClick={() => navigate(`/families/${patient.familyId}`)}
                        >
                          View Family
                        </button>
                      </p>
                    </div>
                  )}
                  {patient.guardianId && (
                    <div className="info-item">
                      <label>Guardian</label>
                      <p>
                        <button
                          className="btn-link"
                          onClick={() => navigate(`/patients/${patient.guardianId}`)}
                        >
                          View Guardian
                        </button>
                      </p>
                    </div>
                  )}
                  {patient.isDependent && (
                    <div className="info-item">
                      <label>Dependent Status</label>
                      <p>
                        <span className="badge dependent">Dependent</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {patient.notes && (
              <div className="info-section" id="notes">
                <h2>Notes</h2>
                <p className="multiline">{patient.notes}</p>
              </div>
            )}

            {canViewBilling && patient?.id && (
              <div className="info-section" id="billing">
                <div className="info-section-header">
                  <h2>Billing</h2>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => navigate(`/patients/${patient.id}/billing/statement/print`)}
                  >
                    Print statement
                  </button>
                </div>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Account</label>
                    <p>{billingAccounts[0]?.id || 'Not created'}</p>
                  </div>
                  <div className="info-item">
                    <label>Balance</label>
                    <p>
                      {billingAccounts[0]
                        ? new Intl.NumberFormat('en-IE', { style: 'currency', currency: billingAccounts[0].currency }).format(billingAccounts[0].balance)
                        : '—'}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Total charges</label>
                    <p>
                      {new Intl.NumberFormat('en-IE', { style: 'currency', currency: billingAccounts[0]?.currency || 'EUR' }).format(
                        billingItems.reduce((sum, item) => sum + (item.amount || 0), 0)
                      )}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Total payments</label>
                    <p>
                      {new Intl.NumberFormat('en-IE', { style: 'currency', currency: billingAccounts[0]?.currency || 'EUR' }).format(
                        billingPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
                      )}
                    </p>
                  </div>
                  <div className="info-item full-width">
                    <label>Latest statement</label>
                    <p>
                      {billingStatements.length > 0
                        ? billingStatements
                            .slice()
                            .sort((a, b) => (a.statementDate > b.statementDate ? -1 : 1))[0].statementDate
                        : 'No statements issued'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <DPSDrugChecker patientId={patient.id} />

            <div className="info-section" id="system-information">
              <h2>System Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Status</label>
                  <p>
                    {patient.isActive ? (
                      <span className="badge active">Active</span>
                    ) : (
                      <span className="badge inactive">Inactive</span>
                    )}
                  </p>
                </div>
                <div className="info-item">
                  <label>Created</label>
                  <p>{new Date(patient.createdAt).toLocaleString()}</p>
                </div>
                <div className="info-item">
                  <label>Last Updated</label>
                  <p>{new Date(patient.updatedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Floating quick navigation widget rendered alongside, but positioned over the right side */}
          <PatientOverviewQuickNav availableSections={overviewAvailableSections} />
        </>
        )}

        {activeTab === 'billing' && (
          <div className="info-section billing-overview-panel">
            <div className="info-section-header">
              <h2>Billing Activity</h2>
              {patient && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate(`/patients/${patient.id}/billing/statement/print`)}
                >
                  Print statement
                </button>
              )}
            </div>
            <div className="info-grid billing-summary-grid">
              <div className="info-item">
                <label>Account</label>
                <p>{billingAccount?.id || 'Not created'}</p>
              </div>
              <div className="info-item">
                <label>Balance</label>
                <p>{billingAccount ? billingBalanceLabel : '—'}</p>
              </div>
              <div className="info-item">
                <label>Total charges</label>
                <p>{new Intl.NumberFormat('en-IE', { style: 'currency', currency: billingCurrency }).format(
                  billingItems.reduce((sum, item) => sum + (item.amount || 0), 0)
                )}</p>
              </div>
              <div className="info-item">
                <label>Total payments</label>
                <p>{new Intl.NumberFormat('en-IE', { style: 'currency', currency: billingCurrency }).format(
                  billingPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
                )}</p>
              </div>
            </div>
            <div className="billing-table-group">
              <div>
                <h3>Billable Items</h3>
                {billingItems.length === 0 ? (
                  <p className="muted">No billable items recorded.</p>
                ) : (
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Consultation date</th>
                        <th>Status</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td>
                            {item.consultationId && consultationDateById.has(item.consultationId) ? (
                              <button
                                type="button"
                                className="btn-link"
                                onClick={() => navigate(`/consultations/${item.consultationId}`)}
                              >
                                {formatDate(consultationDateById.get(item.consultationId) || '')}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{item.status}</td>
                          <td className="amount">
                            {new Intl.NumberFormat('en-IE', { style: 'currency', currency: item.currency }).format(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div>
                <h3>Payments</h3>
                {billingPayments.length === 0 ? (
                  <p className="muted">No payments recorded.</p>
                ) : (
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Provider</th>
                        <th>Status</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingPayments.map((payment) => (
                        <tr key={payment.id}>
                          <td>{payment.paymentDate.split('T')[0]}</td>
                          <td>{payment.provider}</td>
                          <td>{payment.status}</td>
                          <td className="amount">
                            {new Intl.NumberFormat('en-IE', { style: 'currency', currency: payment.currency }).format(payment.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div>
                <h3>Statements</h3>
                {billingStatements.length === 0 ? (
                  <p className="muted">No statements issued.</p>
                ) : (
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Items</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingStatements.map((statement) => (
                        <tr key={statement.id}>
                          <td>{statement.statementDate}</td>
                          <td>{statement.status}</td>
                          <td>{statement.itemIds.length}</td>
                          <td className="amount">
                            {new Intl.NumberFormat('en-IE', { style: 'currency', currency: billingCurrency }).format(statement.totalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="appointments-content">
            <div className="tab-content-header">
              <h2>Appointments</h2>
              <button
                className="btn-primary"
                onClick={() => setShowAppointmentModal(true)}
                disabled={!canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                + New Appointment
              </button>
            </div>

            {/* DNA History Panel */}
            {patient && patient.dnaCount && patient.dnaCount > 0 && (
              <div className="info-section" id="dna-history">
                <h2>DNA (Did Not Attend) History</h2>
                <div className="dna-summary">
                  <div className="dna-count-badge">
                    <strong>Total DNA Count:</strong> {patient.dnaCount}
                  </div>
                  {patient.dnaHistory && patient.dnaHistory.length > 0 && (
                    <div className="dna-history-list">
                      <h3>Recent DNA Appointments</h3>
                      <table className="dna-history-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Appointment Date</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {patient.dnaHistory.map((dna, idx) => (
                            <tr key={idx}>
                              <td>{formatDate(dna.dnaAt)}</td>
                              <td>{formatDate(dna.appointmentDate)}</td>
                              <td>{dna.dnaReason || <span className="muted">No reason provided</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Appointments List */}
            <div className="info-section">
              <h2>All Appointments</h2>
              {!appointments || appointments.length === 0 ? (
                <div className="empty-state">
                  <p>No appointments found.</p>
                  <button
                    className="btn-primary"
                    onClick={() => setShowAppointmentModal(true)}
                    disabled={!canWrite}
                    title={canWrite ? undefined : 'Secondary records are read-only'}
                  >
                    Schedule First Appointment
                  </button>
                </div>
              ) : (
                <div className="appointments-list">
                  {appointments
                    .sort((a, b) => {
                      const dateA = new Date(`${a.appointmentDate}T${a.startTime}`)
                      const dateB = new Date(`${b.appointmentDate}T${b.startTime}`)
                      return dateB.getTime() - dateA.getTime() // Most recent first
                    })
                    .map((appointment) => {
                      const staff = clinicalStaff?.find((s) => s.id === appointment.staffId)
                      const staffName = staff ? `${staff.firstName} ${staff.lastName}` : 'Unknown Staff'
                      
                      const getStatusBadgeClass = (status: AppointmentStatus) => {
                        switch (status) {
                          case 'scheduled':
                            return 'badge-scheduled'
                          case 'arrived':
                            return 'badge-arrived'
                          case 'in-progress':
                            return 'badge-in-progress'
                          case 'completed':
                            return 'badge-completed'
                          case 'cancelled':
                            return 'badge-cancelled'
                          case 'dna':
                            return 'badge-dna'
                          default:
                            return ''
                        }
                      }

                      return (
                        <div key={appointment.id} className="appointment-item">
                          <div className="appointment-item-header">
                            <div>
                              <h3>
                                {new Date(appointment.appointmentDate).toLocaleDateString()} at {appointment.startTime}
                              </h3>
                              <p className="appointment-staff">With: {staffName}</p>
                            </div>
                            <span className={`badge ${getStatusBadgeClass(appointment.status)}`}>
                              {appointment.status}
                            </span>
                          </div>
                          <div className="appointment-item-details">
                            <div className="detail-row">
                              <label>Type:</label>
                              <span>{appointment.type}</span>
                            </div>
                            <div className="detail-row">
                              <label>Duration:</label>
                              <span>{appointment.duration} minutes</span>
                            </div>
                            {appointment.reason && (
                              <div className="detail-row">
                                <label>Reason:</label>
                                <span>{appointment.reason}</span>
                              </div>
                            )}
                            {appointment.notes && (
                              <div className="detail-row">
                                <label>Notes:</label>
                                <span>{appointment.notes}</span>
                              </div>
                            )}
                            {appointment.status === 'cancelled' && appointment.cancellationReason && (
                              <div className="detail-row">
                                <label>Cancellation Reason:</label>
                                <span className="cancellation-reason-text">{appointment.cancellationReason}</span>
                              </div>
                            )}
                            {appointment.status === 'dna' && (
                              <div className="detail-row">
                                <label>DNA:</label>
                                <span className="dna-reason-text">
                                  {appointment.dnaReason || 'No reason provided'}
                                  {appointment.dnaAt && (
                                    <span className="dna-date">
                                      {' '}({formatDate(appointment.dnaAt)})
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          {appointment.status === 'scheduled' && (
                            <div className="appointment-item-actions">
                              <button
                                className="btn-link btn-warning"
                                onClick={() => setCancelModalAppointment(appointment)}
                                disabled={!canWrite}
                                title={canWrite ? undefined : 'Secondary records are read-only'}
                              >
                                Cancel Appointment
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'consultations' && (
          <div className="consultations-content">
            <div className="tab-content-header">
              <h2>Consultations</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setSelectedConsultation(undefined)
                  setShowConsultationForm(true)
                }}
                disabled={!canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                + New Consultation
              </button>
            </div>
            <ConsultationList
              patientId={patient.id}
              navigateToDetail={true}
              onConsultationClick={(consultation) => {
                if (!canWrite) return
                setSelectedConsultation(consultation)
                setShowConsultationForm(true)
              }}
            />
          </div>
        )}

        {activeTab === 'lab-results' && (
          <div className="lab-results-content">
            <div className="tab-content-header">
              <h2>Lab Results</h2>
              <button
                className="btn-secondary"
                onClick={() => navigate('/lab-results', { state: { patientId: patient.id } })}
              >
                View All Lab Results
              </button>
            </div>
            <LabResultList
              patientId={patient.id}
              navigateToDetail={true}
              onLabResultClick={(labResult) => {
                navigate(`/lab-results/${labResult.id}`)
              }}
            />
          </div>
        )}

        {activeTab === 'vitals' && id && (
          <div className="vitals-content">
            <div className="tab-content-header">
              <h2>Vital Signs</h2>
              <div className="tab-content-header-actions">
                {patient &&
                  patient.dateOfBirth &&
                  (patient.gender === 'male' || patient.gender === 'female') &&
                  calculateAgeInMonths(patient.dateOfBirth) <= 60 && (
                    <button
                      className="btn-secondary"
                      onClick={() => setShowGrowthChartsDialog(true)}
                      type="button"
                    >
                      View Growth Charts
                    </button>
                  )}
                <button
                  className="btn-primary"
                  onClick={() => setShowVitalSignsForm(true)}
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Secondary records are read-only'}
                >
                  Add Vitals
                </button>
              </div>
            </div>
            <div className="vitals-tab-content">
              <div className="vitals-layout-grid">
                <div className="vitals-summary-column">
                  <VitalSignsSummary
                    patientId={id}
                    userPreference={getUnitPreference()}
                  />
                </div>
                <div className="vitals-main-column">
                  <div className="vitals-list-section">
                    <h3>Vital Signs History</h3>
                    <VitalSignsList
                      patientId={id}
                      userPreference={getUnitPreference()}
                    />
                  </div>
                  <div className="vitals-charts-section">
                    <VitalSignsChart
                      patientId={id}
                      vitalType="bloodPressure"
                      userPreference={getUnitPreference()}
                    />
                    <VitalSignsChart
                      patientId={id}
                      vitalType="heartRate"
                      userPreference={getUnitPreference()}
                    />
                    <VitalSignsChart
                      patientId={id}
                      vitalType="temperature"
                      userPreference={getUnitPreference()}
                    />
                    <VitalSignsChart
                      patientId={id}
                      vitalType="respiratoryRate"
                      userPreference={getUnitPreference()}
                    />
                    {patient && (patient.gender === 'male' || patient.gender === 'female') && (
                      <>
                        <VitalSignsChart
                          patientId={id}
                          vitalType="weight"
                          userPreference={getUnitPreference()}
                        />
                        <VitalSignsChart
                          patientId={id}
                          vitalType="height"
                          userPreference={getUnitPreference()}
                        />
                        <VitalSignsChart
                          patientId={id}
                          vitalType="bmi"
                          userPreference={getUnitPreference()}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'assessments' && id && (
          <div className="assessments-content">
            <div className="tab-content-header">
              <h2>Standard Assessments</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  // TODO: Open assessment form
                }}
                disabled={!canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                New Assessment
              </button>
            </div>
            <AssessmentList
              patientId={id}
              onSelect={(_assessment) => {
                // TODO: Show assessment detail
              }}
            />
          </div>
        )}

        {activeTab === 'care-plans' && id && (
          <div className="care-plans-content">
            <div className="tab-content-header">
              <h2>Care Plans</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn-secondary"
                  onClick={() => setShowCarePlanTemplateSelector(true)}
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Secondary records are read-only'}
                >
                  Create from Template
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setShowCarePlanForm(true)}
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Secondary records are read-only'}
                >
                  New Care Plan
                </button>
              </div>
            </div>
            <CarePlanList
              patientId={id}
              onSelect={(plan) => {
                navigate(`/care-plans/${plan.id}`)
              }}
            />
          </div>
        )}

        {activeTab === 'protocols' && id && (
          <div className="protocols-content">
            <div className="tab-content-header">
              <h2>Protocols</h2>
            </div>
            <PatientProtocolList
              patientId={id}
              onEnroll={() => {
                if (!canWrite) return
                setShowProtocolEligibilityModal(true)
              }}
              onExclude={async (enrollment) => {
                if (!canWrite) return
                if (confirm('Are you sure you want to exclude this patient from the protocol?')) {
                  try {
                    await protocolService.excludePatient(id!, enrollment.protocolId)
                    queryClient.invalidateQueries({ queryKey: ['patient-protocols', id] })
                  } catch (error) {
                    alert('Failed to exclude patient from protocol')
                  }
                }
              }}
            />
          </div>
        )}

        {activeTab === 'research-studies' && id && (
          <div className="research-studies-content">
            <div className="tab-content-header">
              <h2>Research Studies</h2>
            </div>
            <PatientResearchStudies
              patientId={id}
              onEnroll={() => {
                if (!canWrite) return
                setShowResearchStudyEnrollmentModal(true)
              }}
              onWithdraw={async (enrollment) => {
                if (!canWrite) return
                const reason = prompt('Please provide a reason for withdrawal:')
                if (reason !== null) {
                  try {
                    await researchStudyService.withdrawPatient(id!, enrollment.studyId, reason)
                    queryClient.invalidateQueries({ queryKey: ['patient-research-studies', id] })
                  } catch (error) {
                    alert('Failed to withdraw patient from study')
                  }
                }
              }}
            />
          </div>
        )}

        {activeTab === 'files' && (
          <div className="files-content">
            <div className="tab-content-header">
              <h2>Patient Files</h2>
              <button
                className="btn-secondary"
                onClick={() => id && openLetterPanel({ patientId: id })}
                disabled={!canWrite || !id}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                Write letter
              </button>
              <button
                className="btn-primary"
                onClick={() => setShowFileUpload(true)}
                disabled={!canWrite}
                title={canWrite ? undefined : 'Secondary records are read-only'}
              >
                Upload File
              </button>
            </div>
            {id && <PatientFileList patientId={id} />}
          </div>
        )}

        {activeTab === 'allergies' && id && (
          <div className="allergies-content">
            <div className="tab-content-header">
              <h2>Allergies, Intolerances & Adverse Reactions</h2>
              <p className="tab-subtitle">
                Record and review drug and non-drug allergies, NKDA status, and full audit history for safer prescribing.
              </p>
            </div>
            <PatientAllergies patientId={id} readOnly={!canWrite} />
          </div>
        )}

        {activeTab === 'medications' && id && (
          <div className="medications-content">
            <div className="tab-content-header">
              <h2>Medications</h2>
              <p className="tab-subtitle">
                Record and review current and past medications prescribed or reported for this patient.
              </p>
            </div>
            <PatientMedications patientId={id} readOnly={!canWrite} />
          </div>
        )}

        {activeTab === 'immunisations' && id && (
          <div className="immunisations-content">
            <PatientImmunisations patientId={id} readOnly={!canWrite} />
          </div>
        )}

        {activeTab === 'prescriptions' && id && (
          <div className="prescriptions-content">
            <div className="tab-content-header">
              <h2>Prescriptions</h2>
              <p className="tab-subtitle">
                Create and manage GMS and private prescriptions, acute and repeat scripts.
              </p>
            </div>
            <PrescriptionsTabContent patientId={id} patient={patient} readOnly={!canWrite} />
          </div>
        )}

        {activeTab === 'referrals' && id && (
          <div className="referrals-content">
            <div className="tab-content-header">
              <h2>Referrals</h2>
              <p className="tab-subtitle">
                View and manage referrals to other healthcare providers.
              </p>
            </div>
            <ReferralsTabContent patientId={id} readOnly={!canWrite} />
          </div>
        )}

        {activeTab === 'pregnancies' && id && (
          <div className="pregnancies-content">
            {selectedPregnancy ? (
              <PregnancyDetail
                pregnancy={selectedPregnancy}
                patientId={id}
                readOnly={!canWrite}
                onBack={() => setSelectedPregnancy(null)}
                onPregnancyUpdated={setSelectedPregnancy}
              />
            ) : (
              <>
                <div className="tab-content-header">
                  <h2>Pregnancies</h2>
                  <p className="tab-subtitle">
                    Record and manage ante-natal and post-natal care, including visits, assessments, interventions, and outcomes.
                  </p>
                </div>
                <PregnancyList
                  patientId={id}
                  readOnly={!canWrite}
                  onPregnancyClick={(pregnancy) => {
                    setSelectedPregnancy(pregnancy)
                  }}
                />
              </>
            )}
          </div>
        )}

        {activeTab === 'tasks' && id && (
          <div className="tasks-content">
            <div className="tab-content-header">
              <h2>Clinical Tasks</h2>
              <div className="tab-content-actions">
                <button
                  className="btn-primary"
                  onClick={() => setShowTaskForm(true)}
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Secondary records are read-only'}
                >
                  + New Task
                </button>
              </div>
            </div>
            {canWrite && showTaskForm && (
              <div className="task-form-container">
                <TaskForm
                  patientId={id}
                  onSubmit={async (data) => {
                    await taskService.createForPatient(id, data)
                    queryClient.invalidateQueries({ queryKey: ['tasks', 'patient', id] })
                    queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
                    setShowTaskForm(false)
                  }}
                  onCancel={() => setShowTaskForm(false)}
                />
              </div>
            )}
            {patientTasks.length === 0 ? (
              <div className="empty-state">
                <p>No tasks found for this patient.</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowTaskForm(true)}
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Secondary records are read-only'}
                >
                  Create First Task
                </button>
              </div>
            ) : (
              <div className="tasks-list">
                <table className="tasks-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Assigned To</th>
                      <th>Due Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientTasks.map((task) => (
                      <tr key={task.id}>
                        <td>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault()
                              navigate(`/tasks/${task.id}`)
                            }}
                          >
                            {task.title}
                          </a>
                        </td>
                        <td>
                          <TaskStatusBadge status={task.status} />
                        </td>
                        <td>
                          <span className={`task-priority-badge priority-${task.priority}`}>
                            {task.priority}
                          </span>
                        </td>
                        <td>
                          {task.assignedTo
                            ? users.find((u) => u.id === task.assignedTo)?.email || task.assignedTo
                            : 'Unassigned'}
                        </td>
                        <td>
                          {task.dueDate ? (
                            <span
                              className={
                                new Date(task.dueDate) < new Date() &&
                                task.status !== 'completed'
                                  ? 'overdue'
                                  : ''
                              }
                            >
                              {formatDate(task.dueDate)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => navigate(`/tasks/${task.id}`)}
                            className="btn-view"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'communications' && id && (
          <div className="communications-content">
            <div className="tab-content-header">
              <h2>Communication Logs</h2>
              <div className="tab-content-actions">
                <button
                  className="btn-primary"
                  onClick={() => setShowCommunicationLogForm(true)}
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Secondary records are read-only'}
                >
                  + Log Communication
                </button>
              </div>
            </div>
            {canWrite && showCommunicationLogForm && (
              <div className="communication-log-form-container">
                <CommunicationLogForm
                  patientId={id}
                  onSubmit={async (data) => {
                    await communicationLogService.create(data)
                    queryClient.invalidateQueries({ queryKey: ['communication-logs', 'patient', id] })
                    queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
                    setShowCommunicationLogForm(false)
                  }}
                  onCancel={() => setShowCommunicationLogForm(false)}
                />
              </div>
            )}
            {communicationLogs.length === 0 ? (
              <div className="empty-state">
                <p>No communication logs found for this patient.</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowCommunicationLogForm(true)}
                  disabled={!canWrite}
                  title={canWrite ? undefined : 'Secondary records are read-only'}
                >
                  Log First Communication
                </button>
              </div>
            ) : (
              <div className="communication-logs-list">
                <table className="communication-logs-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Type</th>
                      <th>Direction</th>
                      <th>Participants</th>
                      <th>Summary</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {communicationLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.dateTime)}</td>
                        <td>{log.communicationType}</td>
                        <td>{log.direction}</td>
                        <td>
                          {log.participants.map((p, idx) => (
                            <span key={idx}>
                              {p.name}
                              {p.role && ` (${p.role})`}
                              {idx < log.participants.length - 1 && ', '}
                            </span>
                          ))}
                        </td>
                        <td>{log.summary}</td>
                        <td>
                          <button
                            onClick={() => {
                              // Could navigate to a detail view or show modal
                              alert(`Communication log: ${log.summary}`)
                            }}
                            className="btn-view"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reminders' && id && (
          <div className="reminders-content">
            <div className="tab-content-header">
              <h2>Reminders & Recalls</h2>
              <p className="tab-subtitle">
                Create and track SMS reminders, recalls, and follow-ups for this patient.
              </p>
            </div>
            <PatientReminders patientId={id} />
          </div>
        )}

        {canWrite && showFileUpload && id && (
          <Modal
            isOpen={canWrite && showFileUpload}
            title="Upload File"
            onClose={() => setShowFileUpload(false)}
          >
            <PatientFileUpload
              patientId={id}
              onSuccess={() => {
                setShowFileUpload(false)
                queryClient.invalidateQueries({ queryKey: ['patient-files', id] })
                queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
              }}
            onCancel={() => setShowFileUpload(false)}
          />
        </Modal>
        )}

        {/* Vital Signs Form Modal */}
        {canWrite && showVitalSignsForm && id && patient && (
          <Modal
            isOpen={canWrite && showVitalSignsForm}
            onClose={() => setShowVitalSignsForm(false)}
            title="Add Vital Signs"
            size="large"
          >
            <VitalSignsForm
              patientId={id}
              patient={patient}
              onSubmit={async (data: VitalSignsFormData) => {
                try {
                  await vitalSignsService.create(data)
                  // Invalidate all vital signs queries for this patient (including charts with dateRange)
                  queryClient.invalidateQueries({ queryKey: ['vital-signs', 'patient', id], exact: false })
                  queryClient.invalidateQueries({ queryKey: ['vital-signs'], exact: false })
                  queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
                  setShowVitalSignsForm(false)
                } catch (error) {
                  console.error('Error creating vital signs:', error)
                  throw error // Re-throw so form can handle it
                }
              }}
              onCancel={() => setShowVitalSignsForm(false)}
              userPreference={getUnitPreference()}
            />
          </Modal>
        )}

        {showGrowthChartsDialog &&
          id &&
          patient &&
          patient.dateOfBirth &&
          (patient.gender === 'male' || patient.gender === 'female') &&
          calculateAgeInMonths(patient.dateOfBirth) <= 60 && (
            <GrowthCentileChartsDialog
              patientId={id}
              patient={patient}
              isOpen={showGrowthChartsDialog}
              onClose={() => setShowGrowthChartsDialog(false)}
            />
          )}

        {activeTab === 'history' && (
          <div className="clinical-history-content">
            <div className="tab-content-header">
              <h2>Clinical History & Preferences</h2>
              <p className="tab-subtitle">
                Comprehensive patient history including conditions, family history, preferences, and consent management for accreditation.
              </p>
            </div>
            {id && canWrite && <PatientClinicalHistory patientId={id} />}
            {id && !canWrite && (
              <div className="patient-readonly-banner">
                Clinical history updates are disabled for secondary records. View or update the primary record instead.
                {primaryRecordId && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => navigate(`/patients/${primaryRecordId}`)}
                    >
                      View primary record
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && id && (
          <div className="audit-history-content">
            <div className="tab-content-header">
              <h2>Audit History</h2>
              <p className="tab-subtitle">
                Complete audit trail of all changes and access to this patient's record, including field-level change tracking for compliance and security.
              </p>
            </div>
            <PatientAuditHistory patientId={id} />
          </div>
        )}

        {activeTab === 'medical-intel' && (
          <div className="medical-intel-content">
            <div className="tab-content-header">
              <h2>Medical Intelligence</h2>
              <p className="tab-subtitle">
                AI-powered insights based on patient medical history, trends, and clinical data. Click the AI Suggestions button to view recommendations.
              </p>
            </div>

            {/* Blood Results Trends Section */}
            <div className="medical-intel-charts-section">
              <h3>Blood Results Trends</h3>
              {bloodSeries.length === 0 ? (
                <div className="empty-state">
                  <p>No numeric blood results available to chart.</p>
                  <p className="empty-state-hint">Add blood lab results to see trends and anomalies.</p>
                </div>
              ) : (
                <div className="intel-charts">
                  {bloodSeries.map((series) => (
                    <div key={series.testName} className="intel-chart-card">
                      <div className="intel-chart-header">
                        <div>
                          <h3>{series.testName}</h3>
                          <p className="intel-chart-meta">
                            Range: {series.min} - {series.max}
                          </p>
                        </div>
                      </div>
                      <MiniTrendChart points={series.points} min={series.min} max={series.max} />
                      <div className="intel-legend">
                        <span className="dot normal" /> Normal
                        <span className="dot abnormal" /> Abnormal
                        <span className="dot critical" /> Critical
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>

      <Modal
        isOpen={canWrite && showConsultationForm}
        onClose={() => {
          setShowConsultationForm(false)
          setSelectedConsultation(undefined)
        }}
        title={selectedConsultation ? 'Edit Consultation' : 'Start Consultation'}
        size={selectedConsultation ? 'large' : 'medium'}
      >
        {selectedConsultation ? (
          <ConsultationForm
            patientId={patient.id}
            consultation={selectedConsultation}
            onSuccess={() => {
              setShowConsultationForm(false)
              setSelectedConsultation(undefined)
              queryClient.invalidateQueries({ queryKey: ['consultations', id] })
              queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
            }}
            onCancel={() => {
              setShowConsultationForm(false)
              setSelectedConsultation(undefined)
            }}
          />
        ) : (
          <QuickStartConsultationForm
            patientId={patient.id}
            onCancel={() => {
              setShowConsultationForm(false)
            }}
          />
        )}
      </Modal>

      <Modal
        isOpen={canWrite && showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        title="Book Appointment"
        size="medium"
      >
        <AppointmentScheduler
          initialPatientId={patient.id}
          onSuccess={() => {
            setShowAppointmentModal(false)
            queryClient.invalidateQueries({ queryKey: ['appointments'] })
            queryClient.invalidateQueries({ queryKey: ['appointments', 'patient', id] })
            queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
          }}
          onCancel={() => setShowAppointmentModal(false)}
        />
      </Modal>

      {cancelModalAppointment && (
        <AppointmentCancelModal
          appointment={{
            id: cancelModalAppointment.id,
            patientName: formatPatientName(patient as any),
            staffName: clinicalStaff?.find(s => s.id === cancelModalAppointment.staffId) 
              ? `${clinicalStaff.find(s => s.id === cancelModalAppointment.staffId)!.firstName} ${clinicalStaff.find(s => s.id === cancelModalAppointment.staffId)!.lastName}`
              : 'Unknown Staff',
            appointmentDate: cancelModalAppointment.appointmentDate,
            startTime: cancelModalAppointment.startTime,
            type: cancelModalAppointment.type,
          }}
          isOpen={!!cancelModalAppointment}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelModalAppointment(null)}
          isLoading={cancelAppointmentMutation.isPending}
        />
      )}

      {/* Care Plan Template Selector Modal */}
      {canWrite && showCarePlanTemplateSelector && id && (
        <CarePlanTemplateSelector
          patientId={id}
          onClose={() => setShowCarePlanTemplateSelector(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['care-plans', id] })
          }}
        />
      )}

      {/* Care Plan Form Modal */}
      <Modal
        isOpen={canWrite && showCarePlanForm}
        onClose={() => setShowCarePlanForm(false)}
        title="Create Care Plan"
        size="large"
      >
        <CarePlanForm
          patientId={id!}
          onSuccess={() => {
            setShowCarePlanForm(false)
            queryClient.invalidateQueries({ queryKey: ['care-plans', id] })
          }}
          onCancel={() => setShowCarePlanForm(false)}
        />
      </Modal>

      {/* Protocol Eligibility Modal */}
      <Modal
        isOpen={canWrite && showProtocolEligibilityModal}
        onClose={() => setShowProtocolEligibilityModal(false)}
        title="Check Protocol Eligibility"
        size="large"
      >
        <ProtocolEligibilityModal
          patientId={id!}
          onEnroll={() => {
            setShowProtocolEligibilityModal(false)
            queryClient.invalidateQueries({ queryKey: ['patient-protocols', id] })
          }}
          onClose={() => setShowProtocolEligibilityModal(false)}
        />
      </Modal>

      {/* Research Study Enrollment Modal */}
      <Modal
        isOpen={canWrite && showResearchStudyEnrollmentModal}
        onClose={() => setShowResearchStudyEnrollmentModal(false)}
        title="Enroll in Research Study"
        size="large"
      >
        <ResearchStudyEnrollmentModal
          patientId={id!}
          onSuccess={() => {
            setShowResearchStudyEnrollmentModal(false)
            queryClient.invalidateQueries({ queryKey: ['patient-research-studies', id] })
          }}
          onCancel={() => setShowResearchStudyEnrollmentModal(false)}
        />
      </Modal>
    </div>
  )
}

// Care Plan Form Component
function CarePlanForm({ patientId, onSuccess, onCancel }: { patientId: string; onSuccess: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
  const createMutation = useMutation({
    mutationFn: (data: CarePlanFormData) => carePlanService.create(data),
    onSuccess: () => {
      onSuccess()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate({
      patientId,
      title,
      description,
      startDate,
      status: 'active',
      goals: [],
      interventions: [],
      outcomes: [],
    })
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Title <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
          placeholder="e.g., Diabetes Management Plan"
        />
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
          placeholder="Describe the care plan"
        />
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Start Date <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
        <button type="button" onClick={onCancel} style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '4px', background: '#6b7280', color: 'white', cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="submit" disabled={createMutation.isPending} style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '4px', background: '#2563eb', color: 'white', cursor: 'pointer' }}>
          {createMutation.isPending ? 'Creating...' : 'Create Care Plan'}
        </button>
      </div>
    </form>
  )
}

// Protocol Eligibility Modal Component
function ProtocolEligibilityModal({ patientId, onEnroll, onClose }: { patientId: string; onEnroll: () => void; onClose: () => void }) {
  const { data: protocols } = useQuery<Protocol[]>({
    queryKey: ['protocols'],
    queryFn: () => protocolService.getAll(),
  })
  const [selectedProtocol, setSelectedProtocol] = useState<string>('')
  const [eligibilityResult, setEligibilityResult] = useState<any>(null)
  const [isChecking, setIsChecking] = useState(false)

  const checkEligibility = async () => {
    if (!selectedProtocol) return
    setIsChecking(true)
    try {
      const result = await protocolService.checkEligibility(patientId, selectedProtocol)
      setEligibilityResult(result)
    } catch (error) {
      alert('Failed to check eligibility')
    } finally {
      setIsChecking(false)
    }
  }

  const handleEnroll = async () => {
    if (!selectedProtocol) return
    try {
      await protocolService.enrollPatient(patientId, selectedProtocol)
      onEnroll()
    } catch (error) {
      alert('Failed to enroll patient')
    }
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Select Protocol <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <select
          value={selectedProtocol}
          onChange={(e) => setSelectedProtocol(e.target.value)}
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
        >
          <option value="">-- Select a protocol --</option>
          {protocols?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          onClick={checkEligibility}
          disabled={!selectedProtocol || isChecking}
          style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '4px', background: '#2563eb', color: 'white', cursor: 'pointer' }}
        >
          {isChecking ? 'Checking...' : 'Check Eligibility'}
        </button>
      </div>
      {eligibilityResult && (
        <div style={{ marginBottom: '1rem', padding: '1rem', background: eligibilityResult.eligible ? '#d1fae5' : '#fee2e2', borderRadius: '4px' }}>
          <p style={{ fontWeight: 500, marginBottom: '0.5rem' }}>
            {eligibilityResult.eligible ? '✓ Patient is eligible' : '✗ Patient is not eligible'}
          </p>
          <p style={{ fontSize: '0.875rem' }}>{eligibilityResult.reason}</p>
          {eligibilityResult.eligible && (
            <button
              type="button"
              onClick={handleEnroll}
              style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', border: 'none', borderRadius: '4px', background: '#16a34a', color: 'white', cursor: 'pointer' }}
            >
              Enroll Patient
            </button>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
        <button type="button" onClick={onClose} style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '4px', background: '#6b7280', color: 'white', cursor: 'pointer' }}>
          Close
        </button>
      </div>
    </div>
  )
}

// Research Study Enrollment Modal Component
function ResearchStudyEnrollmentModal({ patientId, onSuccess, onCancel }: { patientId: string; onSuccess: () => void; onCancel: () => void }) {
  const { data: studies } = useQuery<ResearchStudy[]>({
    queryKey: ['research-studies'],
    queryFn: () => researchStudyService.getAll(),
  })
  const [selectedStudy, setSelectedStudy] = useState<string>('')
  const [consentObtained, setConsentObtained] = useState(false)
  const [consentDate, setConsentDate] = useState(new Date().toISOString().split('T')[0])
  const [notes, setNotes] = useState('')
  const enrollMutation = useMutation({
    mutationFn: (data: any) => researchStudyService.enrollPatient(patientId, selectedStudy, data),
    onSuccess: () => {
      onSuccess()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedStudy) return
    enrollMutation.mutate({
      consentObtained,
      consentDate: consentObtained ? consentDate : undefined,
      notes,
    })
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Select Study <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <select
          value={selectedStudy}
          onChange={(e) => setSelectedStudy(e.target.value)}
          required
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
        >
          <option value="">-- Select a study --</option>
          {studies?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={consentObtained}
            onChange={(e) => setConsentObtained(e.target.checked)}
          />
          <span>Consent Obtained</span>
        </label>
      </div>
      {consentObtained && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Consent Date</label>
          <input
            type="date"
            value={consentDate}
            onChange={(e) => setConsentDate(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
          />
        </div>
      )}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
          placeholder="Additional notes about enrollment"
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
        <button type="button" onClick={onCancel} style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '4px', background: '#6b7280', color: 'white', cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="submit" disabled={enrollMutation.isPending || !selectedStudy} style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '4px', background: '#2563eb', color: 'white', cursor: 'pointer' }}>
          {enrollMutation.isPending ? 'Enrolling...' : 'Enroll Patient'}
        </button>
      </div>
    </form>
  )
}

// Small inline chart for trends
type TrendPoint = { date: Date; value: number; flag?: string }
interface MiniTrendChartProps {
  points: TrendPoint[]
  min: number
  max: number
}

function MiniTrendChart({ points, min, max }: MiniTrendChartProps) {
  if (!points.length) return null
  const width = 320
  const height = 120
  const padding = 12
  const range = max === min ? 1 : max - min

  const getX = (idx: number) => {
    if (points.length === 1) return width / 2
    return padding + (idx / (points.length - 1)) * (width - padding * 2)
  }
  const getY = (value: number) => {
    const normalized = (value - min) / range
    return height - padding - normalized * (height - padding * 2)
  }

  const pathD = points
    .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${getX(idx)} ${getY(p.value)}`)
    .join(' ')

  return (
    <svg className="mini-trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Lab result trend">
      {/* Background grid line */}
      <line
        x1={padding}
        x2={width - padding}
        y1={height - padding}
        y2={height - padding}
        className="chart-grid"
      />
      {/* Trend line */}
      <path d={pathD} className="chart-line" fill="none" />
      {/* Points */}
      {points.map((p, idx) => {
        const isCritical = p.flag === 'critical-high' || p.flag === 'critical-low'
        const isAbnormal = !isCritical && p.flag && p.flag !== 'normal'
        return (
          <circle
            key={idx}
            cx={getX(idx)}
            cy={getY(p.value)}
            r={5}
            className={
              isCritical ? 'chart-point critical' : isAbnormal ? 'chart-point abnormal' : 'chart-point normal'
            }
          >
            <title>
              {p.value} on {p.date.toLocaleDateString()} {p.flag ? `(${p.flag})` : ''}
            </title>
          </circle>
        )
      })}
    </svg>
  )
}
